import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, MotionConfig, useReducedMotion } from 'framer-motion';
import {
  MessageCircle, CalendarCheck, Inbox, BellRing,
  CheckCheck, Phone, Video, Check, Smile, Paperclip, Mic,
  CheckCircle2, Bot, Bell, Globe, Sun, HelpCircle, LogOut,
  LayoutDashboard, Settings, TrendingDown, Clock3,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { WEEKDAY_FULL_ES, MONTH_FULL_ES, capitalize, getDayLabel } from '../../utils/date';

/* ════════════════════════════════════════════════════════════════════════════
   FEATURES — "Todo en un solo agente".
   Una sola historia (la paciente María González) recorre los 4 módulos de la app:
     1. Responde 24/7   → el Agente IA conversa con María y reserva su cita.
     2. Agenda          → esa cita entra sola a la agenda (vía WhatsApp).
     3. Leads           → el mensaje de María queda guardado como lead (filtros reales).
     4. Recordatorios   → María recibe su recordatorio automático.
   Columna izquierda: selector de funciones. Derecha: la app real de RurayHUB
   (barra superior + riel de módulos + registro de actividad). Sin chrome de
   navegador falso, sin estética de "IA genérica".
   ════════════════════════════════════════════════════════════════════════════ */

const TEAL = '#00e5d1';
const PURPLE = '#8b5cf6';
const BLUE = '#3b82f6';
const AMBER = '#fb7185';   // Agenda — coral premium (antes naranja #f59e0b)
const ROSE = '#f43f5e';
const WA_GREEN = '#25D366';

// Segundo color del degradado por acento (riqueza visual en estados activos).
const ACC2: Record<string, string> = {
  [TEAL]: '#37f0dd',
  [AMBER]: '#f9a8d4',   // coral → rosa
  [BLUE]: '#60a5fa',
  [PURPLE]: '#c084fc',
};
const grad2 = (c: string) => ACC2[c] || c;

// 👇 Personalización por cliente.
const CLINIC = { name: 'Clínica Dental Sonríe', emoji: '🦷' };
// 👇 Paciente protagonista de la demo — ficticio, por eso solo iniciales
// (nunca la foto de un desconocido haciendo de "paciente real").
const PATIENT = { name: 'María González', initials: 'MG', color: '#34d399' };

const wait = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));

/* ── Coreografía del "handoff" cita→agenda (tiempos relativos al cambio de módulo).
   La píldora de WhatsApp despega, vuela hasta el hueco de María y, justo cuando
   llega, la cita "cae" en su sitio. Todo se sincroniza con estas constantes. ── */
const SWAP_EXIT = 0.22;        // s · fade-out del módulo anterior (AnimatePresence mode="wait")
const HANDOFF_LAUNCH = 0.6;    // s tras cambiar a Agenda · la píldora despega
const HANDOFF_FLY = 1.0;       // s · vuelo continuo hasta aterrizar en el hueco
const HANDOFF_HOLD = 0.55;     // s · reposa en el hueco antes de desvanecerse
const HANDOFF_CLEAR = (HANDOFF_LAUNCH + HANDOFF_FLY + HANDOFF_HOLD) * 1000; // ms · se desvanece
// La cita de María cae justo cuando la píldora aterriza (+lag de render).
const MARIA_DELAY = HANDOFF_LAUNCH + HANDOFF_FLY - SWAP_EXIT + 0.05;

/* ── Iconos de marca inline (SVG real, sin dependencias externas) ── */
const IconWA: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.967-.94 1.165-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

/* ── Avatar con foto real + fallback a iniciales ── */
const Avatar: React.FC<{ photo?: string; initials: string; color: string; size: number; radius?: number }> = ({ photo, initials, color, size, radius }) => {
  const [err, setErr] = useState(false);
  const r = radius ?? size / 2;
  if (photo && !err) {
    return (
      <img src={photo} alt={initials} onError={() => setErr(true)}
        style={{ width: size, height: size, borderRadius: r, objectFit: 'cover', flexShrink: 0, border: `1px solid ${color}66`, display: 'block' }} />
    );
  }
  return (
    <span style={{ width: size, height: size, borderRadius: r, flexShrink: 0, display: 'grid', placeItems: 'center', fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: size * 0.36, background: `${color}22`, color, border: `1px solid ${color}55` }}>
      {initials}
    </span>
  );
};

/* ─────────────────────────────────────────────────────────────
   PANTALLA 01 · Responde 24/7 — conversación de WhatsApp
   (vista del operador: María = paciente · respuestas = Agente IA)
   ───────────────────────────────────────────────────────────── */
type Msg = { side: 'in' | 'out'; text: React.ReactNode; time: string; typing?: number; pause?: number };
const SCRIPT: Msg[] = [
  { side: 'in', text: <>Hola, ¿atienden hoy? 🦷</>, time: '9:41', pause: 500 },
  { side: 'out', typing: 1300, time: '9:41', text: <>¡Hola! 😊 Sí, tengo <b>15:30</b> o <b>17:00</b> disponible hoy mismo.</> },
  { side: 'in', text: <>15:30 va perfecto</>, time: '9:42', pause: 750 },
  { side: 'out', typing: 1200, time: '9:42', text: <>¡Listo! Te esperamos hoy a las 15:30 🦷 Te envié el recordatorio.</> },
];

const ChatDemo: React.FC = () => {
  const [shown, setShown] = useState(0);
  const [typingSide, setTypingSide] = useState<'in' | 'out' | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      await wait(450);
      for (let i = 0; i < SCRIPT.length; i++) {
        if (!alive) return;
        const m = SCRIPT[i];
        if (m.typing) {
          setTypingSide(m.side);
          await wait(m.typing);
          if (!alive) return;
          setTypingSide(null);
        }
        setShown(i + 1);
        await wait(m.pause ?? 600);
      }
      if (!alive) return;
      setDone(true);
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="wa">
      <div className="wa-header">
        <Avatar initials={PATIENT.initials} color={PATIENT.color} size={40} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="wa-name">{PATIENT.name}</div>
          <div className="wa-online">en línea <span className="wa-online-tag">· paciente</span></div>
        </div>
        <div className="wa-hicons"><Video size={18} /><Phone size={16} /></div>
      </div>

      <div className="wa-agentbar"><Bot size={13} /> Respondido en automático por el <b>Agente IA</b></div>

      <div className="wa-body">
        <div className="wa-enc">🔒 Los mensajes están cifrados de extremo a extremo</div>
        <span className="wa-day">HOY</span>

        {SCRIPT.slice(0, shown).map((m, i) => (
          <motion.div
            key={i}
            className={`wa-row ${m.side}`}
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className={`wa-bub ${m.side}`}>
              {m.side === 'out' && <span className="wa-agtag"><Bot size={10} /> Agente IA</span>}
              <span className="wa-text">{m.text}</span>
              <span className="wa-meta">
                {m.time}
                {m.side === 'out' && <CheckCheck size={15} className="wa-check" />}
              </span>
            </div>
          </motion.div>
        ))}

        <AnimatePresence>
          {typingSide && (
            <motion.div
              className={`wa-row ${typingSide}`}
              initial={{ opacity: 0, y: 8, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.15 } }}
              transition={{ duration: 0.25 }}
            >
              <div className={`wa-bub ${typingSide} wa-typing`}>
                <span className="wa-dot" /><span className="wa-dot" /><span className="wa-dot" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {done && (
            <motion.div
              className="wa-appt"
              initial={{ opacity: 0, y: 10, scale: 0.85 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: 'spring', stiffness: 320, damping: 20 }}
            >
              <CalendarCheck size={14} /> Cita de María agendada · Hoy 15:30
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="wa-input">
        <Smile size={20} className="wa-i-ic" />
        <div className="wa-i-field">El Agente IA responde por ti…</div>
        <Paperclip size={18} className="wa-i-ic" />
        <div className="wa-i-send"><Mic size={17} /></div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   PANTALLA 02 · Agenda — las citas entran una por una; la de María
   (vía WhatsApp) cae al final con énfasis.
   ───────────────────────────────────────────────────────────── */
const DAY_START = 9;
const ROW_H = 52;
const HOURS = [9, 10, 11, 12, 13, 14, 15, 16, 17];
const GRID_H = (HOURS.length - 1) * ROW_H;
const NOW_H = 14.2;

// Encabezado y columnas del calendario: se calculan con la fecha real del
// visitante en vez de quedar fijos ("Junio 2026", "MIÉ 11") — un demo con
// fecha vieja es de las señales más fuertes de que el sitio está abandonado.
// Las citas de ejemplo (nombres, horarios) sí se quedan fijas: son ilustrativas,
// no dependen de qué día sea hoy. (Arrays compartidos con HeroAnimation.tsx
// en src/utils/date.ts, para no duplicar los nombres en español dos veces.)

type Ev = { start: number; dur: number; name: string; type: string; color: string; isNew?: boolean; fromWA?: boolean };
const DAY_EVENTS: { today?: boolean; secondary?: boolean; events: Ev[] }[] = [
  {
    today: true, events: [
      { start: 9, dur: 0.75, name: 'Sofía R.', type: 'Limpieza', color: TEAL },
      { start: 11.5, dur: 0.5, name: 'Pedro L.', type: 'Revisión', color: BLUE },
      { start: 15.5, dur: 1.25, name: 'María G.', type: 'Revisión', color: AMBER, isNew: true, fromWA: true },
    ],
  },
  {
    secondary: true, events: [
      { start: 10.5, dur: 1, name: 'Carlos R.', type: 'Ortodoncia', color: PURPLE },
      { start: 16, dur: 0.75, name: 'Lucía M.', type: 'Endodoncia', color: ROSE },
    ],
  },
  {
    secondary: true, events: [
      { start: 9.5, dur: 0.75, name: 'José T.', type: 'Extracción', color: ROSE },
      { start: 14, dur: 1.5, name: 'Diego F.', type: 'Implante', color: TEAL },
    ],
  },
];

const fmtH = (h: number) => {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${hh}:${mm.toString().padStart(2, '0')}`;
};

const CalendarDemo: React.FC = () => {
  const today = new Date();
  const DAYS = DAY_EVENTS.map((d, i) => ({ ...d, ...getDayLabel(i) }));

  // Orden de entrada global por hora → las citas aparecen "solitas", una a una.
  const seq: { key: string; start: number }[] = [];
  DAYS.forEach((d, di) => d.events.forEach((e, ei) => { if (!e.isNew) seq.push({ key: `${di}-${ei}`, start: e.start }); }));
  seq.sort((a, b) => a.start - b.start);
  const dmap: Record<string, number> = {};
  seq.forEach((o, idx) => { dmap[o.key] = idx; });
  // La cita de María cae justo cuando la píldora del handoff llega a su hueco.
  const newDelay = MARIA_DELAY;

  return (
    <div className="cal">
      <div className="cal-head">
        <div>
          <div className="cal-h-title">Hoy · {WEEKDAY_FULL_ES[today.getDay()]} {today.getDate()}</div>
          <div className="cal-h-sub">{capitalize(MONTH_FULL_ES[today.getMonth()])} {today.getFullYear()} · Agenda del consultorio</div>
        </div>
        <div className="cal-h-toggle">
          <span>Día</span><span className="on">3 días</span><span>Semana</span>
        </div>
      </div>

      <div className="cal-board">
        <div className="cal-axis">
          <div className="cal-axis-sp" />
          <div className="cal-axis-lines" style={{ height: GRID_H }}>
            {HOURS.map((h, i) => (
              <span key={h} style={{ top: i * ROW_H }}>{h}:00</span>
            ))}
          </div>
        </div>

        {DAYS.map((day, di) => (
          <div className={`cal-day ${day.secondary ? 'sec' : ''} ${day.today ? 'today' : ''}`} key={day.dow}>
            <div className={`cal-dayhd ${day.today ? 'today' : ''}`}>
              <span className="cal-dow">{day.dow}</span>
              <span className="cal-dn">{day.n}</span>
            </div>
            <div className="cal-cells" style={{ height: GRID_H }}>
              {HOURS.slice(0, -1).map((_, i) => (
                <div key={i} className="cal-line" style={{ top: (i + 1) * ROW_H }} />
              ))}

              {day.today && (
                <div className="cal-now" style={{ top: (NOW_H - DAY_START) * ROW_H }}>
                  <span className="cal-now-dot" />
                  <span className="cal-now-time">{fmtH(NOW_H)}</span>
                </div>
              )}

              {day.events.map((e, i) => {
                const top = (e.start - DAY_START) * ROW_H + 2;
                const height = e.dur * ROW_H - 4;
                const short = e.dur <= 0.5;
                const order = dmap[`${di}-${i}`] ?? 0;
                return (
                  <motion.div
                    key={i}
                    className={`cal-ev ${e.isNew ? 'new' : ''} ${short ? 'short' : ''}`}
                    style={{
                      top, height,
                      background: `color-mix(in srgb, ${e.color} 62%, #0a0e16 38%)`,
                      boxShadow: '0 6px 14px -10px rgba(0,0,0,0.6)',
                    }}
                    // La cita nueva entra solo con escala (centro invariable) para que
                    // la píldora del handoff apunte exactamente a su hueco final.
                    initial={e.isNew ? { opacity: 0, scale: 0.6 } : { opacity: 0, y: 10, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={e.isNew
                      ? { delay: newDelay, type: 'spring', stiffness: 300, damping: 17, mass: 0.7 }
                      : { delay: 0.25 + order * 0.1, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <span className="cal-ev-l1">
                      <b style={{ color: '#fff' }}>{e.name}</b>
                      <span className="cal-ev-t">{fmtH(e.start)}</span>
                    </span>
                    {e.fromWA
                      ? <span className="cal-ev-type"><IconWA size={9} /> {e.type} · vía WhatsApp</span>
                      : (!short && <span className="cal-ev-type">{e.type}</span>)}
                    {e.isNew && <span className="cal-ev-tag" style={{ background: e.color }}>nueva</span>}
                    {e.isNew && (
                      <motion.span
                        className="cal-ev-ring" style={{ borderColor: e.color }}
                        animate={{ opacity: [0.7, 0], scale: [1, 1.06] }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut', delay: newDelay + 0.3 }}
                      />
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   PANTALLA 03 · Leads — bandeja de WhatsApp con FILTROS por estado
   ───────────────────────────────────────────────────────────── */
type Tag = 'nuevo' | 'cita';
const TAG_META: Record<Tag, { color: string; label: string }> = {
  nuevo: { color: TEAL,  label: 'Nuevo' },
  cita:  { color: AMBER, label: 'Con cita' },
};
type Convo = { name: string; initials: string; av: string; tag: Tag; preview: string; time: string; replied?: boolean; unread?: number; fresh?: boolean; photo?: string };
const CONVOS: Convo[] = [
  { name: 'María González', initials: 'MG', av: '#34d399', tag: 'nuevo', preview: '¿Tienen cita para hoy? Me duele una muela', time: 'ahora', replied: true, fresh: true },
  { name: 'Carlos Ruiz', initials: 'CR', av: '#8b5cf6', tag: 'cita', preview: 'Perfecto, nos vemos el martes a las 10', time: '3m', replied: true },
  { name: 'Lucía Martín', initials: 'LM', av: '#f59e0b', tag: 'nuevo', preview: 'Quería preguntar por una limpieza dental', time: '11m', unread: 2 },
  { name: 'Andrea Pérez', initials: 'AP', av: '#ec4899', tag: 'nuevo', preview: 'Quiero info sobre blanqueamiento 😁', time: '16m', replied: true },
  { name: 'José Torres', initials: 'JT', av: '#0ea5e9', tag: 'cita', preview: '¡Muchas gracias, excelente atención!', time: '29m', replied: true },
];

const FILTERS: { k: 'all' | Tag; label: string; color: string }[] = [
  { k: 'all', label: 'Todos', color: BLUE },
  { k: 'nuevo', label: 'Nuevos', color: TEAL },
  { k: 'cita', label: 'Citas', color: AMBER },
];

const InboxDemo: React.FC = () => {
  const [tag, setTag] = useState<'all' | Tag>('all');
  const list = CONVOS.filter((c) => tag === 'all' || c.tag === tag);
  const sub = tag === 'all'
    ? `${CONVOS.length} conversaciones · WhatsApp`
    : `${list.length} ${list.length === 1 ? 'conversación' : 'conversaciones'} · ${TAG_META[tag].label}`;

  return (
    <div className="ibx">
      <div className="ibx-head">
        <div>
          <div className="ibx-title">Bandeja de WhatsApp</div>
          <div className="ibx-sub">{sub}</div>
        </div>
        <div className="ibx-filters">
          {FILTERS.map((flt) => {
            const on = tag === flt.k;
            return (
              <button
                key={flt.k}
                className={`ibx-fbtn wide ${on ? 'on' : ''}`}
                onClick={() => setTag(flt.k)}
                style={on ? { color: '#0b0f16', background: '#fff', borderColor: '#fff' } : undefined}
                aria-pressed={on}
                title={flt.label}
              >
                {flt.label}
              </button>
            );
          })}
        </div>
      </div>

      <motion.div className="ibx-list" layout>
        <AnimatePresence mode="popLayout">
          {list.map((c, idx) => (
            <motion.div
              layout
              className={`ibx-row ${c.fresh ? 'fresh' : ''}`}
              key={c.name}
              initial={{ opacity: 0, scale: 0.96, y: -6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.32, delay: idx * 0.05, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="ibx-av-wrap">
                <Avatar photo={c.photo} initials={c.initials} color={c.av} size={38} />
                <span className="ibx-badge" style={{ color: WA_GREEN }}><IconWA size={11} /></span>
              </div>
              <div className="ibx-mid">
                <div className="ibx-r1">
                  <span className="ibx-name">{c.name}</span>
                  <span className="ibx-time">{c.time}</span>
                </div>
                <div className="ibx-r2">
                  <span className="ibx-prev">{c.preview}</span>
                  {c.replied
                    ? <span className="ibx-replied"><CheckCheck size={13} /></span>
                    : <span className="ibx-unread">{c.unread}</span>}
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   PANTALLA 04 · Recordatorios — pantalla de bloqueo de María
   ───────────────────────────────────────────────────────────── */
const ReminderDemo: React.FC = () => {
  const today = new Date();
  const remDate = `${WEEKDAY_FULL_ES[today.getDay()]}, ${today.getDate()} de ${MONTH_FULL_ES[today.getMonth()]}`;

  return (
  <div className="rem">
    <div className="rem-stats">
      <motion.div className="rem-stat"
        initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      >
        <span className="rem-stat-ic" style={{ background: `${PURPLE}1f`, color: PURPLE }}><TrendingDown size={16} /></span>
        <div>
          <div className="rem-stat-n">-80%</div>
          <div className="rem-stat-l">Inasistencias</div>
        </div>
      </motion.div>
      <motion.div className="rem-stat"
        initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.65, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      >
        <span className="rem-stat-ic" style={{ background: `${TEAL}1f`, color: TEAL }}><Clock3 size={16} /></span>
        <div>
          <div className="rem-stat-n">24h antes</div>
          <div className="rem-stat-l">Recordatorio automático</div>
        </div>
      </motion.div>
    </div>

    <div className="rem-phone">
      <div className="rem-notch" />
      <div className="rem-lock">
        <div className="rem-time">9:00</div>
        <div className="rem-date">{remDate}</div>
      </div>

      <div className="rem-stack">
        <motion.div
          className="rem-noti"
          initial={{ opacity: 0, y: -34, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.4, type: 'spring', stiffness: 260, damping: 22 }}
        >
          <div className="rem-noti-hd">
            <span className="rem-appicon wa"><IconWA size={15} /></span>
            <span className="rem-app">WhatsApp</span>
            <span className="rem-now">ahora</span>
          </div>
          <div className="rem-noti-title">{CLINIC.name}</div>
          <div className="rem-noti-body">🦷 María, recuerda tu cita <b>mañana a las 15:30</b>. ¿Confirmas tu asistencia?</div>
          <motion.div
            className="rem-noti-actions"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.45 }}
          >
            <span className="rem-btn yes"><Check size={13} strokeWidth={3} /> Confirmar</span>
            <span className="rem-btn no">Reprogramar</span>
          </motion.div>
        </motion.div>

        <motion.div
          className="rem-noti ghost"
          initial={{ opacity: 0, y: -16, scale: 0.93 }}
          animate={{ opacity: 0.75, y: 0, scale: 1 }}
          transition={{ delay: 0.75, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="rem-appicon cal"><CalendarCheck size={15} /></span>
          <div className="rem-ghost-tx">
            <span>Google Calendar</span>
            <small>María G. · Revisión · mañana 15:30</small>
          </div>
        </motion.div>
      </div>
    </div>
  </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Config de las 4 funciones / módulos
   ───────────────────────────────────────────────────────────── */
interface Feature {
  id: string; module: string; title: string; desc: string; accent: string; pastel: string;
  icon: React.ReactNode; duration: number; Demo: React.FC;
  foot: { text: React.ReactNode; time: string };
}
// Función en vez de const module-level para poder usar t() — los strings de
// mock-up de cada demo (chats, calendario, leads) se mantienen en español a
// propósito: son contenido ilustrativo de una conversación ficticia, no copy
// de marketing. Lo que sí se traduce es la capa que el visitante lee siempre
// (títulos/descripciones del selector y el pie de cada pantalla).
const getFeatures = (t: TFunction): Feature[] => [
  {
    id: 'responde', module: t('features.module_chat', 'Conversaciones'), title: t('features.title_chat', 'Responde 24/7'), accent: TEAL, pastel: '#8FF0E0',
    desc: t('features.desc_chat', 'Atiende cada mensaje en segundos mientras operas, duermes o descansas. Sin pausas, sin horarios.'),
    icon: <MessageCircle size={19} />, duration: 8500, Demo: ChatDemo,
    foot: { text: <>{t('features.foot_chat_pre', 'El Agente IA respondió a')} <b>María</b> {t('features.foot_chat_post', 'y reservó su cita de las 15:30.')}</>, time: t('features.foot_chat_time', 'ahora') },
  },
  {
    id: 'agenda', module: t('features.module_calendar', 'Agenda'), title: t('features.title_calendar', 'Agenda citas sola'), accent: AMBER, pastel: '#FBD0D9',
    desc: t('features.desc_calendar', 'Propone horarios, confirma y deja la cita en tu Google Calendar. Tú no tocas nada.'),
    icon: <CalendarCheck size={19} />, duration: 7500, Demo: CalendarDemo,
    foot: { text: <>{t('features.foot_calendar_pre', 'La cita de')} <b>María</b> {t('features.foot_calendar_post', 'entró sola a la agenda y se sincronizó con Google Calendar.')}</>, time: t('features.foot_calendar_time', 'hace 1 m') },
  },
  {
    id: 'centraliza', module: t('features.module_inbox', 'Leads'), title: t('features.title_inbox', 'Centraliza tus pacientes'), accent: BLUE, pastel: '#BFE3FF',
    desc: t('features.desc_inbox', 'Cada chat de WhatsApp ordenado por estado (nuevo, con cita…). Nunca más un paciente perdido entre tantos mensajes.'),
    icon: <Inbox size={19} />, duration: 7000, Demo: InboxDemo,
    foot: { text: <>{t('features.foot_inbox_pre', 'El mensaje de')} <b>María</b> {t('features.foot_inbox_post', 'quedó guardado como lead — 4 de 5 respondidos.')}</>, time: t('features.foot_inbox_time', 'hace 3 m') },
  },
  {
    id: 'recordatorios', module: t('features.module_reminder', 'Recordatorios'), title: t('features.title_reminder', 'Recordatorios automáticos'), accent: PURPLE, pastel: '#E2D6FA',
    desc: t('features.desc_reminder', 'Envía confirmaciones y recordatorios solo. Reduce las ausencias a casi cero.'),
    icon: <BellRing size={19} />, duration: 6500, Demo: ReminderDemo,
    foot: { text: <><b>María</b> {t('features.foot_reminder_post', 'recibió su recordatorio automático el día previo a la cita.')}</>, time: t('features.foot_reminder_time', 'hace 5 m') },
  },
];

/* ════════════════════════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
   ════════════════════════════════════════════════════════════════════════════ */
const Features: React.FC = () => {
  const { t } = useTranslation();
  const FEATURES = React.useMemo(() => getFeatures(t), [t]);
  const [active, setActive] = useState(0);
  // Barra de progreso animada por DOM directo (no useState) — se actualiza cada 50ms
  // y hacerlo con setState causaría 20 renders/seg además de un set-state-in-effect
  // al resetear a 0 en cada cambio de `active`. progBarRef siempre apunta al único
  // div .fs-prog visible (el del item activo), React lo reasigna solo al cambiar `active`.
  const progBarRef = React.useRef<HTMLDivElement>(null);
  const pausedRef = React.useRef(false);
  const progRef = React.useRef(0);
  const lastRef = React.useRef(0);
  const appRef = React.useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();

  // Handoff: mide el hueco exacto de la cita de María en la agenda y lleva la píldora ahí.
  const screenRef = React.useRef<HTMLDivElement>(null);
  const [handoff, setHandoff] = useState<{ sx: number; sy: number; x: number; y: number } | null>(null);
  useEffect(() => {
    // No reseteamos `handoff` a null aquí directamente (evita un setState
    // síncrono en el cuerpo del efecto) — en vez de eso, el render de más
    // abajo solo lo muestra cuando `active === 1`, así que un valor viejo
    // en `handoff` mientras se cambia de pantalla simplemente no se pinta.
    if (active !== 1 || reduce) return;
    const id = setTimeout(() => {
      const sc = screenRef.current; if (!sc) return;
      const ev = sc.querySelector('.cal-ev.new') as HTMLElement | null; if (!ev) return;
      const sr = sc.getBoundingClientRect();
      const er = ev.getBoundingClientRect();
      setHandoff({ sx: sr.width / 2, sy: 22, x: er.left - sr.left + er.width / 2, y: er.top - sr.top + er.height / 2 });
    }, HANDOFF_LAUNCH * 1000);
    // Tras reposar en el hueco, la píldora se desvanece (exit) y deja la cita en su sitio.
    const idClear = setTimeout(() => setHandoff(null), HANDOFF_CLEAR);
    return () => { clearTimeout(id); clearTimeout(idClear); };
  }, [active, reduce]);

  useEffect(() => {
    progRef.current = 0;
    if (progBarRef.current) progBarRef.current.style.width = '0%';
    lastRef.current = Date.now();
    const dur = FEATURES[active].duration;
    const id = setInterval(() => {
      const now = Date.now();
      const dt = now - lastRef.current;
      lastRef.current = now;
      if (pausedRef.current) return;
      progRef.current = Math.min(100, progRef.current + (dt / dur) * 100);
      if (progBarRef.current) progBarRef.current.style.width = `${progRef.current}%`;
      if (progRef.current >= 100) {
        progRef.current = 0;
        setActive((a) => (a + 1) % FEATURES.length);
      }
    }, 50);
    return () => clearInterval(id);
  }, [active, FEATURES]);

  const select = (i: number) => {
    if (i === active) return;
    progRef.current = 0;
    setActive(i);
  };

  const f = FEATURES[active];
  const ActiveDemo = f.Demo;
  const accent = f.accent;

  return (
    <MotionConfig reducedMotion="user">
    <section id="features" style={{ padding: 'clamp(4.5rem, 9vh, 7.5rem) 0', position: 'relative' }}>
      <style>{`
        .feat-eyebrow {
          display: inline-block; font-size: 0.72rem; font-weight: 800; letter-spacing: 0.18em;
          text-transform: none; color: #000; margin-bottom: 1.2rem;
          padding: 9px 20px; border-radius: 999px; border: none;
          background: #5EEAD4; font-family: var(--font-heading);
        }

        /* Mismo tratamiento que Hero/HowItWorks: degradado del logo en vez de recuadro. */
        .feat-highlight {
          font-weight: 800;
          background: linear-gradient(135deg, #00f0ff 0%, #9d6bff 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
        }

        .fs-grid { display: grid; grid-template-columns: 366px 1fr; gap: 1.5rem; align-items: stretch; }
        @media (max-width: 980px) { .fs-grid { grid-template-columns: 1fr; gap: 1.1rem; } }

        /* ── Columna izquierda · selector de funciones ── */
        .fs-list { display: flex; flex-direction: column; gap: 0.7rem; }
        @media (max-width: 980px) {
          .fs-list { flex-direction: row; overflow-x: auto; padding-bottom: 6px; scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; }
          .fs-list::-webkit-scrollbar { height: 0; }
        }
        .fs-item {
          position: relative; text-align: left; cursor: pointer; overflow: hidden;
          background: rgba(255,255,255,0.02); border: 1px solid var(--border-light);
          border-radius: 16px; padding: 1.05rem 1.15rem 1.05rem 1.3rem; width: 100%; font-family: var(--font-body); color: inherit;
          transition: background 0.35s ease, border-color 0.35s ease, transform 0.35s var(--ease-out-expo);
        }
        .fs-item:hover { background: rgba(255,255,255,0.04); transform: translateY(-2px); }
        .fs-item:active { transform: translateY(0) scale(0.99); }
        .fs-item:focus-visible, .rail-item:focus-visible, .ibx-fbtn:focus-visible { outline: 2px solid ${TEAL}; outline-offset: 2px; }
        .rail-item:active { transform: scale(0.93); }
        @media (max-width: 980px) { .fs-item { min-width: 244px; scroll-snap-align: start; padding: 0.9rem 1rem 0.9rem 1.15rem; } }
        /* Blanco/negro para marcar el activo, igual que el resto del sitio —
           el color por función vive solo en el ícono (pastel, ver .fs-icon). */
        .fs-item.on { border-color: var(--text-primary); background: rgba(255,255,255,0.06); }
        [data-theme='light'] .fs-item.on { background: rgba(0,0,0,0.04); }
        .fs-stripe { position: absolute; left: 0; top: 16px; bottom: 16px; width: 3px; border-radius: 0 3px 3px 0; background: var(--text-primary); }
        .fs-item-row { display: flex; align-items: center; gap: 0.8rem; }
        .fs-icon {
          width: 40px; height: 40px; border-radius: 11px; display: grid; place-items: center; flex-shrink: 0;
          color: #000; border: none;
        }
        .fs-num { font-family: var(--font-heading); font-size: 0.68rem; font-weight: 700; color: var(--text-muted); letter-spacing: 0.08em; margin-left: auto; flex-shrink: 0; transition: color 0.35s ease; }
        .fs-item-title { font-family: var(--font-heading); font-weight: 700; font-size: 1rem; color: var(--text-secondary); line-height: 1.2; transition: color 0.35s ease; }
        .fs-item.on .fs-item-title { color: var(--text-primary); }
        .fs-item.on .fs-num { color: var(--text-primary); }
        .fs-item-desc { font-family: var(--font-body); font-size: 0.84rem; line-height: 1.5; color: var(--text-secondary); overflow: hidden; }
        @media (max-width: 980px) { .fs-item-desc { display: none; } }
        .fs-prog-track { position: absolute; left: 0; bottom: 0; height: 2px; width: 100%; background: rgba(255,255,255,0.05); }
        .fs-prog { position: absolute; left: 0; bottom: 0; height: 2px; }

        /* ════════════ App de RurayHUB ════════════ */
        .app {
          position: relative; display: flex; flex-direction: column; overflow: hidden;
          border-radius: 20px; border: 1px solid rgba(255,255,255,0.09);
          background: linear-gradient(180deg, #12141b, #0a0c11);
          box-shadow: 0 50px 110px -50px rgba(0,0,0,0.95), inset 0 1px 0 rgba(255,255,255,0.05);
        }
        .app-top { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-bottom: 1px solid rgba(255,255,255,0.07); background: rgba(255,255,255,0.015); }
        .app-ws { display: flex; flex-direction: column; min-width: 0; line-height: 1.25; }
        .app-ws-name { font-family: var(--font-heading); font-weight: 700; font-size: 0.86rem; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .app-ws-sub { font-size: 0.68rem; color: rgba(255,255,255,0.5); }
        .app-top-r { margin-left: auto; display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
        @keyframes appPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }

        /* ── Botonera de la app (notificaciones, idioma, tema, ayuda, soporte, admin, salir) ── */
        .app-tool-btn {
          position: relative; display: inline-flex; align-items: center; gap: 4px;
          width: 28px; height: 28px; justify-content: center; padding: 0;
          border-radius: 8px; border: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.04);
          color: rgba(255,255,255,0.7); cursor: pointer; transition: background 0.2s ease;
        }
        .app-tool-btn:hover { background: rgba(255,255,255,0.08); }
        .app-tool-lang { width: auto; padding: 0 8px; font-size: 0.66rem; font-weight: 700; font-family: var(--font-heading); }
        .app-tool-dot { position: absolute; top: 4px; right: 5px; width: 6px; height: 6px; border-radius: 50%; background: #f43f5e; box-shadow: 0 0 0 2px #12141b; }
        .app-support-btn {
          display: inline-flex; align-items: center; gap: 5px; font-size: 0.68rem; font-weight: 700; font-family: var(--font-heading);
          color: #6ee7b7; background: rgba(37,211,102,0.1); border: 1px solid rgba(37,211,102,0.26); padding: 5px 10px; border-radius: 999px;
          white-space: nowrap;
        }
        .app-admin { display: inline-flex; align-items: center; gap: 8px; padding-left: 8px; border-left: 1px solid rgba(255,255,255,0.1); }
        .app-admin-avatar {
          width: 28px; height: 28px; border-radius: 9px; flex-shrink: 0; display: grid; place-items: center;
          font-family: var(--font-heading); font-weight: 800; font-size: 0.68rem; color: ${TEAL};
          background: rgba(0,229,209,0.12); border: 1px solid rgba(0,229,209,0.3);
        }
        .app-admin-text { display: flex; flex-direction: column; line-height: 1.2; white-space: nowrap; }
        .app-admin-name { font-family: var(--font-heading); font-weight: 700; font-size: 0.72rem; color: #fff; }
        .app-admin-role { font-size: 0.6rem; color: rgba(255,255,255,0.42); }
        .app-logout-btn {
          display: inline-flex; align-items: center; gap: 4px; font-size: 0.68rem; font-weight: 600; font-family: var(--font-heading);
          color: rgba(255,255,255,0.6); background: transparent; border: none; cursor: pointer; padding: 4px 2px; white-space: nowrap;
          transition: color 0.2s ease;
        }
        .app-logout-btn:hover { color: #fff; }
        @media (max-width: 640px) { .app-admin-text, .app-tool-lang, .app-logout-btn, .app-tool-btn:not(:last-of-type) { display: none; } }
        @media (max-width: 460px) { .app-support-btn { display: none; } }

        .app-body { display: flex; flex: 1; min-height: 0; }
        .app-rail { width: 60px; flex-shrink: 0; border-right: 1px solid rgba(255,255,255,0.07); background: rgba(0,0,0,0.25); display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 14px 0; }
        @media (max-width: 600px) { .app-rail { display: none; } }
        .rail-item { position: relative; width: 40px; height: 40px; border-radius: 11px; display: grid; place-items: center; cursor: pointer; background: transparent; border: 1px solid transparent; color: rgba(255,255,255,0.38); transition: all 0.3s var(--ease-out-expo); }
        .rail-item:hover { color: rgba(255,255,255,0.75); background: rgba(255,255,255,0.04); }
        .rail-item-static { cursor: default; }
        .rail-item-static:hover { color: rgba(255,255,255,0.38); background: transparent; }
        .rail-pip { position: absolute; left: -14px; top: 50%; transform: translateY(-50%); width: 3px; height: 20px; border-radius: 0 3px 3px 0; }
        .rail-badge {
          position: absolute; top: 3px; right: 3px; min-width: 14px; height: 14px; padding: 0 3px;
          border-radius: 999px; background: #f43f5e; color: #fff; font-size: 0.55rem; font-weight: 800;
          display: grid; place-items: center; box-shadow: 0 0 0 2px rgba(0,0,0,0.25);
        }
        .rail-divider { width: 22px; height: 1px; background: rgba(255,255,255,0.08); margin: 2px 0; flex-shrink: 0; }
        .app-rail-sp { flex: 1; }
        .rail-brand { width: 20px; height: 20px; object-fit: contain; margin-top: 10px; opacity: 0.85; flex-shrink: 0; }

        .app-screen { position: relative; flex: 1; min-width: 0; height: 600px; overflow: hidden; display: flex; align-items: center; justify-content: center; padding: 1.4rem; }
        @media (max-width: 600px) { .app-screen { height: auto; min-height: 470px; padding: 1.1rem; } }
        .app-glow { position: absolute; top: -120px; right: -90px; width: 300px; height: 300px; border-radius: 50%; filter: blur(80px); opacity: 0.12; pointer-events: none; transition: background 0.6s ease; }
        .app-screen-inner { position: relative; z-index: 1; width: 100%; display: flex; justify-content: center; }
        .app-handoff { position: absolute; z-index: 5; display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; font-family: var(--font-heading); font-size: 0.72rem; font-weight: 700; color: #fff; background: linear-gradient(135deg, ${WA_GREEN}, #0e8f73); padding: 6px 12px; border-radius: 999px; box-shadow: 0 12px 26px -8px rgba(0,0,0,0.85), inset 0 0 0 1px rgba(255,255,255,0.14); pointer-events: none; }

        .app-foot { display: flex; align-items: center; gap: 11px; padding: 12px 16px; border-top: 1px solid rgba(255,255,255,0.07); background: rgba(255,255,255,0.015); }
        .app-foot-ic { width: 28px; height: 28px; border-radius: 9px; display: grid; place-items: center; flex-shrink: 0; }
        .app-foot-tx { font-size: 0.82rem; color: rgba(255,255,255,0.62); line-height: 1.35; min-width: 0; }
        .app-foot-tx b { color: #fff; font-weight: 700; }
        .app-foot-time { margin-left: auto; flex-shrink: 0; font-size: 0.68rem; font-weight: 600; color: rgba(255,255,255,0.38); font-family: var(--font-heading); }

        /* ═══════════ Chat WhatsApp ═══════════ */
        .wa { border-radius: 14px; overflow: hidden; border: 1px solid rgba(255,255,255,0.08); box-shadow: 0 26px 54px -28px rgba(0,0,0,0.9); width: 100%; }
        .wa-header { display: flex; align-items: center; gap: 11px; background: #1f2c33; padding: 0.65rem 0.9rem; }
        .wa-name { font-family: var(--font-heading); font-weight: 700; font-size: 0.9rem; color: #e9edef; line-height: 1.25; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .wa-online { font-size: 0.7rem; color: ${WA_GREEN}; font-weight: 600; margin-top: 1px; }
        .wa-online-tag { color: #8696a0; font-weight: 500; }
        .wa-hicons { display: flex; gap: 20px; color: #aebac1; flex-shrink: 0; padding-right: 2px; }
        .wa-agentbar { display: flex; align-items: center; justify-content: center; gap: 7px; background: #131d22; color: #9bb6c4; font-size: 0.64rem; font-weight: 600; padding: 0.4rem 0.7rem; border-bottom: 1px solid rgba(255,255,255,0.05); font-family: var(--font-body); text-align: center; line-height: 1.3; }
        .wa-agentbar b { color: #7fe7d6; font-weight: 700; }
        .wa-body { background-color: #0b141a; background-image: radial-gradient(rgba(255,255,255,0.022) 1px, transparent 1px); background-size: 16px 16px; padding: 0.75rem 0.85rem 0.85rem; display: flex; flex-direction: column; gap: 0.45rem; min-height: 250px; }
        .wa-enc { align-self: center; display: inline-flex; align-items: center; gap: 4px; font-size: 0.6rem; color: #cbb373; background: rgba(255,225,150,0.05); padding: 0.3rem 0.75rem; border-radius: 7px; text-align: center; max-width: 92%; line-height: 1.4; }
        .wa-day { align-self: center; font-size: 0.62rem; font-weight: 700; letter-spacing: 0.05em; color: #97a8b3; background: rgba(255,255,255,0.07); padding: 0.2rem 0.75rem; border-radius: 7px; margin: 0.2rem 0; }
        .wa-row { display: flex; }
        .wa-row.in { justify-content: flex-start; }
        .wa-row.out { justify-content: flex-end; }
        .wa-bub { position: relative; max-width: min(82%, 400px); padding: 0.42rem 0.62rem 0.36rem; display: flex; flex-direction: column; font-size: 0.87rem; line-height: 1.42; color: #e9edef; font-family: var(--font-body); box-shadow: 0 1px 1px rgba(0,0,0,0.22); }
        .wa-bub.in { background: #1f2c33; border-radius: 3px 9px 9px 9px; }
        .wa-bub.out { background: #005c4b; border-radius: 9px 3px 9px 9px; }
        .wa-bub.in::before { content: ''; position: absolute; top: 0; left: -6px; width: 10px; height: 13px; background: #1f2c33; clip-path: polygon(100% 0, 100% 100%, 0 0); }
        .wa-bub.out::before { content: ''; position: absolute; top: 0; right: -6px; width: 10px; height: 13px; background: #005c4b; clip-path: polygon(0 0, 100% 0, 0 100%); }
        .wa-bub b { font-weight: 700; }
        .wa-agtag { display: inline-flex; align-items: center; gap: 3px; font-family: var(--font-heading); font-size: 0.58rem; font-weight: 800; letter-spacing: 0.02em; color: #7fe7d6; margin-bottom: 2px; text-transform: uppercase; }
        .wa-meta { align-self: flex-end; display: inline-flex; align-items: center; gap: 3px; font-size: 0.62rem; color: #97a8b3; margin-top: 2px; line-height: 1; }
        .wa-bub.out .wa-meta { color: rgba(233,237,239,0.68); }
        .wa-check { color: #53bdeb; }
        .wa-typing { flex-direction: row; gap: 4px; padding: 0.55rem 0.72rem; align-items: center; }
        .wa-dot { width: 6px; height: 6px; border-radius: 50%; background: #8696a0; display: inline-block; animation: waDot 1.3s infinite ease-in-out; }
        .wa-dot:nth-child(2) { animation-delay: 0.18s; } .wa-dot:nth-child(3) { animation-delay: 0.36s; }
        @keyframes waDot { 0%,80%,100% { transform: translateY(0); opacity: 0.4; } 40% { transform: translateY(-4px); opacity: 1; } }
        .wa-appt { align-self: center; margin-top: 0.3rem; display: inline-flex; align-items: center; gap: 7px; font-family: var(--font-heading); font-size: 0.72rem; font-weight: 700; color: #6ee7b7; background: rgba(37,211,102,0.1); border: 1px solid rgba(37,211,102,0.28); padding: 0.36rem 0.85rem; border-radius: 999px; }
        .wa-input { display: flex; align-items: center; gap: 9px; background: #1f2c33; padding: 0.45rem 0.7rem; }
        .wa-i-ic { color: #8696a0; flex-shrink: 0; }
        .wa-i-field { flex: 1; background: #2a3942; color: #8696a0; font-size: 0.78rem; padding: 0.45rem 0.8rem; border-radius: 999px; }
        .wa-i-send { width: 32px; height: 32px; border-radius: 50%; background: ${WA_GREEN}; color: #0b141a; display: grid; place-items: center; flex-shrink: 0; }

        /* ═══════════ Calendario ═══════════ */
        .cal { width: 100%; }
        .cal-head { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 1rem; gap: 1rem; }
        .cal-h-title { font-family: var(--font-heading); font-weight: 800; font-size: 1.05rem; letter-spacing: -0.02em; color: #fff; }
        .cal-h-sub { font-size: 0.72rem; color: rgba(255,255,255,0.42); margin-top: 3px; }
        .cal-h-toggle { display: inline-flex; gap: 6px; flex-shrink: 0; }
        .cal-h-toggle span { font-size: 0.68rem; font-weight: 700; font-family: var(--font-heading); color: rgba(255,255,255,0.55); background: rgba(255,255,255,0.05); border: 1px solid var(--border-light); padding: 6px 13px; border-radius: 9px; transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease; }
        .cal-h-toggle span.on { background: #fff; color: #0b0f16; border-color: #fff; }
        .cal-board { display: flex; border: 1px solid var(--border-light); border-radius: 16px; overflow: hidden; background: linear-gradient(180deg, rgba(255,255,255,0.025), rgba(255,255,255,0.006)); box-shadow: inset 0 1px 0 rgba(255,255,255,0.04); }
        .cal-axis { width: 42px; flex-shrink: 0; border-right: 1px solid var(--border-light); }
        .cal-axis-sp { height: 44px; border-bottom: 1px solid var(--border-light); }
        .cal-axis-lines { position: relative; }
        .cal-axis-lines span { position: absolute; right: 7px; transform: translateY(-6px); font-size: 0.58rem; font-weight: 700; color: rgba(255,255,255,0.34); font-family: var(--font-heading); }
        .cal-day { flex: 1; min-width: 0; border-right: 1px solid var(--border-light); transition: background 0.3s ease; }
        .cal-day:last-child { border-right: none; }
        .cal-day.today { background: ${AMBER}05; }
        .cal-dayhd { height: 44px; display: flex; align-items: center; justify-content: center; gap: 6px; border-bottom: 1px solid var(--border-light); background: rgba(255,255,255,0.02); position: relative; }
        .cal-dayhd.today { background: linear-gradient(180deg, ${AMBER}1c, ${AMBER}0a); }
        .cal-dayhd.today::after { content: ''; position: absolute; left: 22%; right: 22%; bottom: 0; height: 2px; border-radius: 2px 2px 0 0; background: ${AMBER}; box-shadow: 0 0 8px ${AMBER}99; }
        .cal-dow { font-size: 0.58rem; font-weight: 800; letter-spacing: 0.06em; color: rgba(255,255,255,0.4); }
        .cal-dayhd.today .cal-dow { color: ${AMBER}; }
        .cal-dn { font-family: var(--font-heading); font-size: 0.94rem; font-weight: 700; color: rgba(255,255,255,0.66); }
        .cal-dayhd.today .cal-dn { color: ${AMBER}; }
        .cal-cells { position: relative; }
        .cal-line { position: absolute; left: 0; right: 0; height: 1px; background: rgba(255,255,255,0.045); }
        .cal-now { position: absolute; left: 0; right: 0; height: 2px; background: ${ROSE}; z-index: 4; box-shadow: 0 0 10px -1px ${ROSE}; }
        .cal-now-dot { position: absolute; left: -4.5px; top: -4px; width: 9px; height: 9px; border-radius: 50%; background: ${ROSE}; box-shadow: 0 0 0 3px ${ROSE}26, 0 0 10px ${ROSE}; }
        .cal-now-time { position: absolute; left: 8px; top: -8px; font-family: var(--font-heading); font-size: 0.56rem; font-weight: 800; color: #fff; background: ${ROSE}; padding: 1px 6px; border-radius: 999px; box-shadow: 0 4px 10px -3px ${ROSE}; }
        .cal-ev { position: absolute; left: 4px; right: 4px; border: none; border-radius: 12px; padding: 5px 9px; overflow: hidden; z-index: 3; transition: transform 0.2s ease; }
        .cal-ev-l1 { display: flex; align-items: center; justify-content: space-between; gap: 5px; }
        .cal-ev-l1 b { font-family: var(--font-heading); font-size: 0.74rem; font-weight: 700; letter-spacing: -0.01em; line-height: 1.15; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .cal-ev-t { font-size: 0.58rem; font-weight: 800; line-height: 1.3; flex-shrink: 0; color: #fff; background: rgba(0,0,0,0.22); padding: 0 5px; border-radius: 5px; }
        .cal-ev-type { display: flex; align-items: center; gap: 3px; font-size: 0.64rem; color: rgba(255,255,255,0.75); margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .cal-ev.short { padding-top: 5px; padding-bottom: 5px; }
        .cal-ev-tag { position: absolute; top: 4px; right: 4px; font-size: 0.48rem; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; color: #1a1205; padding: 2px 6px; border-radius: 999px; box-shadow: 0 2px 6px -2px rgba(0,0,0,0.5); }
        .cal-ev-ring { position: absolute; inset: -2px; border: 1.5px solid; border-radius: 11px; pointer-events: none; }
        @media (max-width: 620px) { .cal-day.sec { display: none; } }

        /* ═══════════ Leads ═══════════ */
        .ibx { width: 100%; }
        .ibx-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 0.8rem; margin-bottom: 1rem; }
        .ibx-title { font-family: var(--font-heading); font-weight: 800; font-size: 1.05rem; letter-spacing: -0.02em; color: #fff; }
        .ibx-sub { font-size: 0.72rem; color: rgba(255,255,255,0.42); margin-top: 3px; }
        .ibx-filters { display: inline-flex; align-items: center; gap: 5px; flex-shrink: 0; }
        .ibx-fbtn { display: grid; place-items: center; cursor: pointer; font-family: var(--font-heading); font-weight: 700; font-size: 0.66rem; color: rgba(255,255,255,0.38); background: rgba(255,255,255,0.04); border: 1px solid var(--border-light); border-radius: 999px; width: 26px; height: 26px; padding: 0; transition: all 0.25s ease; }
        .ibx-fbtn.wide { width: auto; padding: 0 11px; }
        .ibx-fbtn:hover { color: rgba(255,255,255,0.75); background: rgba(255,255,255,0.07); }
        .ibx-list { display: flex; flex-direction: column; border: 1px solid var(--border-light); border-radius: 16px; overflow: hidden; background: linear-gradient(180deg, rgba(255,255,255,0.025), rgba(255,255,255,0.006)); box-shadow: inset 0 1px 0 rgba(255,255,255,0.04); }
        .ibx-row { position: relative; display: flex; align-items: center; gap: 0.8rem; padding: 0.75rem 0.95rem; border-bottom: 1px solid var(--border-light); transition: background 0.25s ease; }
        .ibx-row:last-child { border-bottom: none; }
        .ibx-row:hover { background: rgba(255,255,255,0.025); }
        .ibx-row.fresh { background: rgba(52,211,153,0.06); }
        .ibx-row.fresh::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 2px; background: ${WA_GREEN}; }
        .ibx-av-wrap { position: relative; flex-shrink: 0; }
        .ibx-badge { position: absolute; right: -3px; bottom: -3px; width: 18px; height: 18px; border-radius: 50%; background: #0d0d0f; border: 2px solid #0d0d0f; display: grid; place-items: center; }
        .ibx-mid { flex: 1; min-width: 0; }
        .ibx-r1 { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
        .ibx-name { font-family: var(--font-heading); font-weight: 700; font-size: 0.84rem; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ibx-time { font-size: 0.64rem; color: rgba(255,255,255,0.38); flex-shrink: 0; }
        .ibx-r2 { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 2px; }
        .ibx-prev { font-size: 0.76rem; color: rgba(255,255,255,0.62); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ibx-replied { color: #53bdeb; flex-shrink: 0; display: inline-flex; }
        .ibx-unread { flex-shrink: 0; min-width: 18px; height: 18px; padding: 0 5px; border-radius: 999px; background: ${WA_GREEN}; color: #03190f; font-size: 0.64rem; font-weight: 800; display: grid; place-items: center; font-family: var(--font-heading); }

        /* ═══════════ Recordatorio ═══════════ */
        .rem { position: relative; width: 100%; min-height: 460px; display: flex; align-items: center; justify-content: center; gap: clamp(1.5rem, 5vw, 3.5rem); flex-wrap: wrap-reverse; }
        .rem-stats { display: flex; flex-direction: column; gap: 0.9rem; flex-shrink: 0; }
        .rem-stat { display: flex; align-items: center; gap: 0.7rem; background: rgba(255,255,255,0.03); border: 1px solid var(--border-light); border-radius: 14px; padding: 0.7rem 1.1rem; min-width: 190px; }
        .rem-stat-ic { width: 34px; height: 34px; border-radius: 10px; display: grid; place-items: center; flex-shrink: 0; }
        .rem-stat-n { font-family: var(--font-heading); font-weight: 800; font-size: 1.05rem; letter-spacing: -0.02em; color: #fff; line-height: 1.15; }
        .rem-stat-l { font-size: 0.72rem; color: rgba(255,255,255,0.5); margin-top: 1px; }
        .rem-phone { position: relative; z-index: 1; width: 100%; max-width: 300px; flex-shrink: 0; border-radius: 30px; padding: 1.3rem 0.85rem 1.4rem; background: radial-gradient(120% 80% at 50% 0%, rgba(139,92,246,0.16), rgba(10,12,18,0.6)); border: 1px solid rgba(255,255,255,0.1); box-shadow: 0 34px 70px -30px rgba(0,0,0,0.95), inset 0 1px 0 rgba(255,255,255,0.06); }
        .rem-notch { position: absolute; top: 10px; left: 50%; transform: translateX(-50%); width: 84px; height: 6px; border-radius: 999px; background: rgba(255,255,255,0.18); }
        .rem-lock { text-align: center; margin-bottom: 1rem; }
        .rem-time { font-family: var(--font-heading); font-weight: 300; font-size: 3rem; line-height: 1; color: #fff; letter-spacing: -0.02em; }
        .rem-date { font-size: 0.78rem; color: rgba(255,255,255,0.6); margin-top: 4px; }
        .rem-stack { display: flex; flex-direction: column; gap: 10px; }
        .rem-noti { position: relative; border-radius: 16px; padding: 0.72rem 0.82rem; background: rgba(40,44,52,0.82); backdrop-filter: blur(14px); border: 1px solid rgba(255,255,255,0.08); box-shadow: 0 14px 30px -16px rgba(0,0,0,0.8); }
        .rem-noti.ghost { display: flex; align-items: center; gap: 9px; padding: 0.58rem 0.78rem; }
        .rem-ghost-tx { display: flex; flex-direction: column; min-width: 0; }
        .rem-ghost-tx span { font-size: 0.72rem; font-weight: 700; color: #e9edef; font-family: var(--font-heading); }
        .rem-ghost-tx small { font-size: 0.64rem; color: rgba(255,255,255,0.5); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .rem-noti-hd { display: flex; align-items: center; gap: 8px; margin-bottom: 0.45rem; }
        .rem-appicon { width: 24px; height: 24px; border-radius: 7px; display: grid; place-items: center; flex-shrink: 0; color: #fff; }
        .rem-appicon.wa { background: ${WA_GREEN}; }
        .rem-appicon.cal { background: linear-gradient(135deg, ${TEAL}, ${PURPLE}); }
        .rem-app { font-size: 0.64rem; font-weight: 800; letter-spacing: 0.04em; color: rgba(255,255,255,0.7); text-transform: uppercase; }
        .rem-now { margin-left: auto; font-size: 0.64rem; color: rgba(255,255,255,0.45); }
        .rem-noti-title { font-family: var(--font-heading); font-weight: 700; font-size: 0.88rem; color: #fff; margin-bottom: 0.22rem; }
        .rem-noti-body { font-size: 0.8rem; line-height: 1.45; color: rgba(233,237,239,0.85); }
        .rem-noti-body b { color: #fff; }
        .rem-noti-actions { display: flex; gap: 0.5rem; margin-top: 0.75rem; }
        .rem-btn { flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 5px; font-size: 0.72rem; font-weight: 700; font-family: var(--font-heading); padding: 0.48rem; border-radius: 10px; }
        .rem-btn.yes { background: rgba(37,211,102,0.16); border: 1px solid rgba(37,211,102,0.4); color: #6ee7b7; }
        .rem-btn.no { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); color: rgba(255,255,255,0.65); }
      `}</style>

      <div className="container">
        {/* Encabezado */}
        <div style={{ textAlign: 'center', maxWidth: 900, margin: '0 auto 3.2rem' }}>
          <motion.h2
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: 0.1, duration: 0.6 }}
            style={{ fontSize: 'clamp(1.5rem, 4.4vw, 3.5rem)', fontWeight: 300, color: 'var(--text-primary)', marginBottom: '1.2rem', lineHeight: 1.12, letterSpacing: '-0.03em' }}>
            <span style={{ whiteSpace: 'nowrap' }}>{t('features.h1', 'Todo lo que tu clínica necesita,')}</span><br />
            <span className="feat-highlight">{t('features.h2', 'en un solo agente.')}</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: 0.2, duration: 0.6 }}
            style={{ color: 'var(--text-secondary)', fontSize: '1.08rem', lineHeight: 1.6, margin: 0 }}>
            {t('features.subtitle', 'Deja de perder pacientes por no contestar a tiempo. RurayHUB trabaja por ti, desde el primer mensaje hasta la cita confirmada.')}
          </motion.p>
        </div>

        {/* ── Spotlight ── */}
        <motion.div
          className="fs-grid"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          onMouseEnter={() => { pausedRef.current = true; }}
          onMouseLeave={() => { pausedRef.current = false; }}
        >
          {/* Selector de funciones */}
          <div className="fs-list">
            {FEATURES.map((feat, i) => {
              const on = i === active;
              return (
                <button
                  key={feat.id}
                  className={`fs-item ${on ? 'on' : ''}`}
                  onClick={() => select(i)}
                  aria-pressed={on}
                >
                  {on && <span className="fs-stripe" />}
                  <div className="fs-item-row">
                    <span className="fs-icon" style={{ background: feat.pastel }}>
                      {feat.icon}
                    </span>
                    <span className="fs-item-title">{feat.title}</span>
                    <span className="fs-num">0{i + 1}</span>
                  </div>

                  <AnimatePresence initial={false}>
                    {on && (
                      <motion.div className="fs-item-desc"
                        initial={{ height: 0, opacity: 0, marginTop: 0 }}
                        animate={{ height: 'auto', opacity: 1, marginTop: 10 }}
                        exit={{ height: 0, opacity: 0, marginTop: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
                        {feat.desc}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {on && (
                    <>
                      <div className="fs-prog-track" />
                      <div ref={progBarRef} className="fs-prog"
                        style={{ width: '0%', background: `linear-gradient(90deg, ${feat.accent}, ${grad2(feat.accent)})` }} />
                    </>
                  )}
                </button>
              );
            })}
          </div>

          {/* App de RurayHUB */}
          <div className="app" ref={appRef}>
            <div className="app-top">
              <div className="app-ws">
                <span className="app-ws-name">{CLINIC.name}</span>
                <span className="app-ws-sub">RurayHUB · Plan Pro</span>
              </div>
              <div className="app-top-r">
                <button className="app-tool-btn" aria-label="Notificaciones">
                  <Bell size={15} />
                  <span className="app-tool-dot" />
                </button>
                <button className="app-tool-btn app-tool-lang" aria-label="Idioma">
                  <Globe size={14} /> ES
                </button>
                <button className="app-tool-btn" aria-label="Tema">
                  <Sun size={15} />
                </button>
                <button className="app-tool-btn" aria-label="Ayuda">
                  <HelpCircle size={15} />
                </button>
                <span className="app-support-btn"><IconWA size={13} /> Soporte</span>
                <span className="app-admin">
                  <span className="app-admin-avatar">VR</span>
                  <span className="app-admin-text">
                    <span className="app-admin-name">Valeria Ríos</span>
                    <span className="app-admin-role">Administradora</span>
                  </span>
                </span>
                <button className="app-logout-btn"><LogOut size={13} /> Salir</button>
              </div>
            </div>

            <div className="app-body">
              <div className="app-rail">
                <button className="rail-item rail-item-static rail-home" aria-label="Inicio" title="Inicio">
                  <LayoutDashboard size={18} />
                </button>

                <span className="rail-divider" />

                {FEATURES.map((feat, i) => {
                  const on = i === active;
                  return (
                    <button
                      key={feat.id}
                      className={`rail-item ${on ? 'on' : ''}`}
                      onClick={() => select(i)}
                      aria-label={feat.module}
                      title={feat.module}
                      style={on ? { color: feat.accent, background: `linear-gradient(135deg, ${feat.accent}26, ${grad2(feat.accent)}0d)`, borderColor: `${feat.accent}3a` } : undefined}
                    >
                      {feat.icon}
                      {feat.id === 'recordatorios' && <span className="rail-badge">1</span>}
                      {on && <span className="rail-pip" style={{ background: feat.accent }} />}
                    </button>
                  );
                })}

                <span className="rail-divider" />

                <button className="rail-item rail-item-static" aria-label="Configuración" title="Configuración">
                  <Settings size={18} />
                </button>

                <span className="app-rail-sp" />
                <img src="/ruraylogotipo.png" alt="RurayHUB" className="rail-brand" />
              </div>

              <div className="app-screen" ref={screenRef}>
                <div className="app-glow" style={{ background: `radial-gradient(circle at 68% 4%, ${accent}, ${grad2(accent)})` }} />
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active}
                    className="app-screen-inner"
                    initial={{ opacity: 0, y: 16, scale: 0.99 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -12, transition: { duration: SWAP_EXIT, ease: [0.4, 0, 1, 1] } }}
                    transition={{
                      default: { type: 'spring', stiffness: 150, damping: 20, mass: 0.6 },
                      opacity: { duration: 0.34, ease: [0.16, 1, 0.3, 1] },
                    }}
                  >
                    <ActiveDemo />
                  </motion.div>
                </AnimatePresence>

                {/* Handoff: la cita "vuela" del chat hasta su hueco exacto en la agenda */}
                <AnimatePresence>
                  {active === 1 && handoff && (
                    <motion.div
                      key="handoff"
                      className="app-handoff"
                      initial={{ opacity: 0, left: handoff.sx, top: handoff.sy, x: '-50%', y: '-50%', scale: 0.72 }}
                      animate={{ opacity: 1, left: handoff.x, top: handoff.y, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.82, transition: { duration: 0.3, ease: [0.4, 0, 1, 1] } }}
                      // Arco continuo, sin escalones: la horizontal sale rápida y asienta
                      // (easeOut), la vertical cae acelerando y amortigua al final (easeIn→settle).
                      transition={{
                        opacity: { duration: 0.26, ease: 'easeOut' },
                        left: { duration: HANDOFF_FLY, ease: [0.16, 0.84, 0.3, 1] },
                        top: { duration: HANDOFF_FLY, ease: [0.42, 0, 0.32, 1] },
                        scale: { type: 'spring', stiffness: 240, damping: 16, mass: 0.7 },
                      }}
                    >
                      <IconWA size={12} /> Cita de María · 15:30
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div className="app-foot" key={`foot-${f.id}`}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}>
                <span className="app-foot-ic" style={{ color: accent, background: `linear-gradient(135deg, ${accent}26, ${grad2(accent)}10)`, border: `1px solid ${accent}3a` }}>
                  <CheckCircle2 size={16} />
                </span>
                <span className="app-foot-tx">{f.foot.text}</span>
                <span className="app-foot-time">{f.foot.time}</span>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
    </MotionConfig>
  );
};

export default Features;
